import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { requireStaff } from '@generated/server/staff.server';

// Live Google Sheet of ballot responses (shared "anyone with the link"). Read fresh every time the report runs.
const SHEET_CSV = 'https://docs.google.com/spreadsheets/d/1AebsbBDx_IkBsYMgMSjk0R0d2VF5tZ1ur7iSImM4an4/gviz/tq?tqx=out:csv&gid=1327535963';

// [sheet column header, label shown in the report]
const CATS = [
  ['Best Restaurant Overall', 'Best Restaurant Overall'], ['Best New Restaurant', 'Best New Restaurant'], ['Best Pizza', 'Best Pizza'],
  ['Best Burger', 'Best Burger'], ['Best Bakery', 'Best Bakery'], ['Best Coffee Shop/Café', 'Best Coffee Shop / Café'], ['Best Bar', 'Best Bar'],
  ['Best Italian Restaurant', 'Best Italian Restaurant'], ['Best Asian Restaurant', 'Best Asian Restaurant'], ['Best Mexican Restaurant', 'Best Mexican Restaurant'],
  ['Best Deli Sandwich', 'Best Deli Sandwich'], ['Best Bodega', 'Best Bodega'], ['Best Boutique', 'Best Boutique'], ['Best Bookstore', 'Best Bookstore'],
  ['Best Vintage Store', 'Best Vintage Store'], ['Best Hardware Store', 'Best Hardware Store'], ['Best Florist', 'Best Florist'],
  ['Best Doctor / Medical Practice', 'Best Doctor / Medical Practice'], ['Best Salon / Barbershop', 'Best Salon / Barbershop'], ['Best Spa / Beauty Service', 'Best Spa / Beauty Service'],
  ['Best Gym / Fitness Studio', 'Best Gym / Fitness Studio'], ['Best Yoga / Pilates Studio', 'Best Yoga / Pilates Studio'],
  ['Best Contractor / Renovation Company', 'Best Contractor / Renovation'], ['Best Realtor / Real Estate Agent', 'Best Realtor / Real Estate Agent'],
  ['Best Architect / Interior Designer', 'Best Architect / Interior Designer'], ['Best Veterinarian', 'Best Veterinarian'], ['Best Pet Shop', 'Best Pet Shop'],
  ['Best Community Organization / Nonprofit', 'Best Community Organization / Nonprofit'], ['Best Live Music / Entertainment Venue', 'Best Live Music / Entertainment Venue'],
  ['Best Art Gallery / Creative Space', 'Best Art Gallery / Creative Space'],
] as const;
const WRITE_IN = 'Did we miss a category? Tell us here:';

function parseCsv(text: string): string[][] {
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charAt(i);
    if (q) { if (ch === '"') { if (text.charAt(i + 1) === '"') { cell += '"'; i++; } else q = false; } else cell += ch; }
    else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text.charAt(i + 1) === '\n') i++; row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}
const SKIP = /^(n\/?a|none|no answer|dunno|\?+|idk|-|lol! none!|we need one ?!?|we don.t have one\.?)$/i;
// Groups spelling variants: "Hook'd on Pets" = "Hooked on pets", "Mark's" = "Marks", "Third Time's a Charm" = "third times the charm".
const norm = (s: string) => s.toLowerCase().replace(/[’'`]/g, '').replace(/\bthe\b|\b(inn|bar|pub|pizzeria|pizza|barbershop|hardware|ace|restaurant)\b/g, '')
  .replace(/hooked/g, 'hookd').replace(/[^a-z0-9]+/g, '').replace(/^3rd/, 'third')
  .replace(/^(stopone|1stop)$/, 'stop1')
  .replace(/^tiburon2$/, 'tiburon')
  .replace(/^thirdtimes?(a|s)?charm$/, 'thirdtimesacharm')
  .replace(/^(mazzones|mazones|mazone|mazzonw|mazzines)$/, 'mazzone')
  .replace(/^courtst(reet)?grocers?$/, 'courtstreetgrocers')
  .replace(/^(rhap|re(d|ps)hookarts?project(rhap)?)$/, 'redhookartproject')
  .replace(/^(hooksonpets|hookonpets|hookdonpet)$/, 'hookdonpets')
  .replace(/^(victoria(alexander|alexandar|alexender)?(atrealtycollective|realtycollective|ofrealtycollective)?|realtycollective(victoria(alexander)?)?|alexander(realtycollective)?)$/, 'realtycollective')
  .replace(/^(flores|floras|florestruck|(flores)?(mexican)?(taco|food)?truc?x?k(onvanbrunt(street)?|acrossrecordshoponvanbrunt)?)$/, 'florestacotruck');
// Official spelling to show for a combined entry (otherwise the most common spelling is shown).
const DISPLAY: Record<string, string> = { hookdonpets: "Hook'd on Pets", florestacotruck: 'Flores Taco Truck', mazzone: 'Mazzone', courtstreetgrocers: 'Court Street Grocers', redhookartproject: 'Red Hook Art Project', thirdtimesacharm: "Third Time's a Charm", realtycollective: 'Realty Collective (Victoria Alexander)' };
// To merge more names George confirms are the same place, add another .replace(/^(variantA|variantB)$/, 'canonical') above.

export const getStarsReport = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), compareTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const res = await fetch(`${SHEET_CSV}&t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) { console.error('[stars] sheet fetch failed', res.status); throw new Error('Could not read the ballot spreadsheet. Check that it is still shared with "Anyone with the link".'); }
    const [header = [], ...body] = parseCsv(await res.text());
    const col = (h: string) => header.findIndex((x) => x.trim().toLowerCase() === h.toLowerCase());
    const rows = body.filter((r) => r.slice(1).some((c) => c.trim()));

    // "Previous report" = standings from ballots received before the comparison date (undated, hand-typed ballots count as earlier).
    const dayOf = (row: string[]) => { const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec((row[0] ?? '').trim()); return m ? `${m[3]}-${m[1]?.padStart(2, '0')}-${m[2]?.padStart(2, '0')}` : null; };
    const nyNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const weekAgo = new Date(nyNow); weekAgo.setDate(weekAgo.getDate() - 7);
    const compareTo = data.compareTo ?? `${weekAgo.getFullYear()}-${String(weekAgo.getMonth() + 1).padStart(2, '0')}-${String(weekAgo.getDate()).padStart(2, '0')}`;
    const prevRows = rows.filter((r) => { const d = dayOf(r); return !d || d < compareTo; });

    const rankFor = (i: number, set: string[][]) => {
      const tally = new Map<string, { votes: number; names: Map<string, number> }>();
      let ballots = 0;
      for (const row of set) {
        const v = i >= 0 ? (row[i] ?? '').trim() : '';
        if (!v || SKIP.test(v)) continue;
        ballots++;
        const k = norm(v) || v.toLowerCase();
        const t = tally.get(k) ?? { votes: 0, names: new Map() };
        t.votes++; t.names.set(v, (t.names.get(v) ?? 0) + 1); tally.set(k, t);
      }
      const ranked = [...tally.entries()].map(([k, t]) => ({ key: k, name: DISPLAY[k] ?? [...t.names.entries()].sort((a, b) => b[1] - a[1] || b[0].length - a[0].length)[0]?.[0] ?? '', votes: t.votes }))
        .sort((a, b) => b.votes - a.votes || a.name.localeCompare(b.name));
      return { ranked, ballots };
    };

    const categories = CATS.map(([h, label]) => {
      const i = col(h);
      const now = rankFor(i, rows);
      const before = rankFor(i, prevRows).ranked.slice(0, 3).map((x) => x.key);
      const top = now.ranked.slice(0, 3).map((x, pos) => {
        const was = before.indexOf(x.key);
        const move: 'up' | 'down' | null = before.length === 0 ? null : was === -1 || was > pos ? 'up' : was < pos ? 'down' : null;
        return { name: x.name, votes: x.votes, move };
      });
      return { label, ballots: now.ballots, top, others: Math.max(0, now.ranked.length - 3) };
    });
    const wi = col(WRITE_IN);
    const writeIns = wi < 0 ? [] : rows.map((r) => (r[wi] ?? '').trim()).filter((s) => !!s && !SKIP.test(s));
    // Ballots per day, from the sheet's Timestamp column (M/D/YYYY h:mm:ss). Every day from the first ballot to today, including zero days.
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const perDay = new Map<string, number>();
    let undated = 0;
    for (const row of rows) {
      const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec((row[0] ?? '').trim());
      if (!m) { undated++; continue; }
      const k = `${m[3]}-${m[1]?.padStart(2, '0')}-${m[2]?.padStart(2, '0')}`;
      perDay.set(k, (perDay.get(k) ?? 0) + 1);
    }
    const daily: { day: string; count: number }[] = [];
    const first = [...perDay.keys()].sort()[0];
    if (first) {
      const nyToday = iso(new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })));
      for (let d = new Date(`${first}T12:00:00`); iso(d) <= nyToday; d.setDate(d.getDate() + 1)) daily.push({ day: iso(d), count: perDay.get(iso(d)) ?? 0 });
    }
    return { total: rows.length, compareTo, prevTotal: prevRows.length, categories, writeIns: [...new Set(writeIns)], daily, undated, generated: new Date().toISOString() };
  });
